import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { Settings as SettingsIcon, Plus, X, Save, BarChart3, Download, DatabaseBackup, UploadCloud, AlertTriangle, Image as ImageIcon, CalendarClock, PlayCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Settings() {
  const [chips, setChips] = useState([]);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  useEffect(() => {
    api.get("/settings/delete-reason-chips")
      .then((r) => setChips(r.data.chips || []))
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  useEffect(() => {
    api.get(`/tickets/deleted/analytics?days=${analyticsDays}`)
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics({ rows: [], total: 0, days: analyticsDays }));
  }, [analyticsDays]);

  const add = () => {
    const v = draft.trim();
    if (!v || chips.includes(v) || chips.length >= 12) return;
    setChips([...chips, v]); setDraft("");
  };
  const remove = (c) => setChips(chips.filter((x) => x !== c));

  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const r = await api.put("/settings/delete-reason-chips", { chips });
      setChips(r.data.chips || []); setOk("Saved.");
      setTimeout(() => setOk(""), 2000);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6">
        <p className="label-eyebrow text-primary">Configuration</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-6 w-6" /> Settings
        </h1>
      </header>

      <section className="sharp-card p-5">
        <h2 className="font-heading text-lg font-semibold">Delete-reason chips</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Presets shown when deleting a service call. Max 12 items. Tap the × to remove.
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span key={c} data-testid={`settings-chip-${c.toLowerCase().replace(/ /g, "-")}`} className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-secondary/40 px-3 py-1 text-xs">
              {c}
              <button
                type="button"
                onClick={() => remove(c)}
                className="rounded-full hover:text-destructive"
                aria-label={`Remove ${c}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {chips.length === 0 && <p className="text-xs text-muted-foreground">No chips yet — add one below.</p>}
        </div>

        <div className="mt-4 flex gap-2">
          <input
            data-testid="settings-chip-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder="e.g. Wrong branch"
            maxLength={40}
            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <button
            type="button"
            data-testid="settings-chip-add"
            onClick={add}
            disabled={!draft.trim() || chips.length >= 12}
            className="inline-flex items-center gap-1 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>

        {err && <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
        {ok && <div className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">{ok}</div>}

        <div className="mt-5 flex justify-end">
          <button
            data-testid="settings-chips-save"
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save chips"}
          </button>
        </div>
      </section>

      <section className="sharp-card mt-6 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Delete-reason analytics
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">Most common reasons captured when tickets were deleted.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="settings-analytics-csv"
              disabled={!analytics || analytics.rows.length === 0}
              onClick={() => {
                const rows = [["Reason", "Count"], ...analytics.rows.map((r) => [r.reason, r.count])];
                const csv = rows.map((r) => r.map((c) => {
                  const s = String(c);
                  return s.includes(",") || s.includes("\"") || s.includes("\n")
                    ? `"${s.replace(/"/g, '""')}"` : s;
                }).join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `delete-reasons-${analytics.days}d.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2.5 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-40"
              title="Download CSV"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <select
              data-testid="settings-analytics-range"
              value={analyticsDays}
              onChange={(e) => setAnalyticsDays(Number(e.target.value))}
              className="rounded-md border border-input bg-background px-2.5 py-1.5 text-xs"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {!analytics ? (
          <p className="mt-6 text-center text-xs text-muted-foreground">Loading…</p>
        ) : analytics.rows.length === 0 ? (
          <p className="mt-6 text-center text-xs text-muted-foreground">No deletions recorded in this range.</p>
        ) : (
          <>
            <p className="mt-2 text-xs text-muted-foreground"><b className="text-foreground">{analytics.total}</b> ticket{analytics.total !== 1 ? "s" : ""} deleted in the last {analytics.days} days.</p>
            <div className="mt-4 h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.rows} layout="vertical" margin={{ left: 20, right: 24, top: 8, bottom: 8 }}>
                  <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="hsl(var(--border) / 0.4)" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="reason" type="category" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12, borderRadius: 6 }}
                    labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </section>

      <BackupRestore />

      <AutoBackupSettings />

      <LetterheadSettings />

      <CompanyProfiles />
    </div>
  );
}

function AutoBackupSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/settings/auto-backup").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  if (!cfg) return null; // super admin only

  const save = async () => {
    setBusy("save"); setErr(""); setMsg("");
    try {
      const r = await api.put("/settings/auto-backup", { enabled: cfg.enabled, hour: Number(cfg.hour), email: cfg.email || "" });
      setCfg(r.data); setMsg("Daily auto backup settings saved.");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(""); }
  };
  const runNow = async () => {
    setBusy("run"); setErr(""); setMsg("");
    try {
      const r = await api.post("/backup/run-now");
      setMsg(`Backup complete — ${r.data.file} (${r.data.records} records) saved to Doc Drive → Backups${r.data.emailed ? `, emailed to ${r.data.emailed} recipient(s)` : ""}.`);
      const g = await api.get("/settings/auto-backup"); setCfg(g.data);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(""); }
  };

  const hlabel = (h) => `${(h % 12) || 12}:00 ${h < 12 ? "AM" : "PM"}`;
  return (
    <section className="sharp-card mt-6 p-5">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
        <CalendarClock className="h-5 w-5" /> Daily Auto Backup
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Every day at your chosen time (IST), a full data backup is saved to Doc Drive → Backups and a notice with all record counts is emailed to your mailbox.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="label-eyebrow mb-1.5 block">Status</label>
          <button
            data-testid="autobackup-toggle"
            onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
            className={`w-full rounded-md border px-3 py-2 text-sm font-semibold ${cfg.enabled ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-600" : "border-border/70 text-muted-foreground"}`}
          >
            {cfg.enabled ? "Enabled" : "Disabled — click to enable"}
          </button>
        </div>
        <div>
          <label className="label-eyebrow mb-1.5 block">Daily time (IST)</label>
          <select
            data-testid="autobackup-hour"
            value={cfg.hour}
            onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {[...Array(24).keys()].map((h) => <option key={h} value={h}>{hlabel(h)}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow mb-1.5 block">Mailbox (email notice)</label>
          <input
            data-testid="autobackup-email"
            type="email"
            placeholder="Defaults to hcitadmin@hcits.in"
            value={cfg.email || ""}
            onChange={(e) => setCfg({ ...cfg, email: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          data-testid="autobackup-save"
          onClick={save}
          disabled={busy === "save"}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          <Save className="h-3.5 w-3.5" /> {busy === "save" ? "Saving…" : "Save settings"}
        </button>
        <button
          data-testid="autobackup-run-now"
          onClick={runNow}
          disabled={busy === "run"}
          className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
        >
          <PlayCircle className="h-3.5 w-3.5" /> {busy === "run" ? "Backing up…" : "Run backup now"}
        </button>
        {cfg.last_run_at && (
          <span className="text-[11px] text-muted-foreground">
            Last run: {new Date(cfg.last_run_at).toLocaleString("en-IN")} {cfg.last_run_result ? `· ${cfg.last_run_result.file} (${cfg.last_run_result.records} records)` : ""}
          </span>
        )}
      </div>
      {msg && <div data-testid="autobackup-message" className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">{msg}</div>}
      {err && <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
    </section>
  );
}

function LetterheadSettings() {
  const [lh, setLh] = useState({ company_name: "", address: "", phone: "", email: "", logo_data: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setLh({ ...lh, [k]: e.target.value });

  useEffect(() => {
    api.get("/settings/letterhead").then((r) => setLh(r.data)).catch(() => {});
  }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 600 * 1024) { setErr("Logo must be under 600KB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setLh((p) => ({ ...p, logo_data: reader.result })); setErr(""); };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.put("/settings/letterhead", lh);
      setLh(r.data);
      setMsg("Letterhead saved. New offer letters will use it automatically.");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="sharp-card mt-6 p-5">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
        <ImageIcon className="h-5 w-5" /> Company Letterhead
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">Logo and address shown at the top of generated Offer Letters (and future branded documents).</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label-eyebrow mb-1.5 block">Company name</label>
          <input data-testid="letterhead-company" value={lh.company_name} onChange={set("company_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="label-eyebrow mb-1.5 block">Phone</label>
          <input data-testid="letterhead-phone" value={lh.phone} onChange={set("phone")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="label-eyebrow mb-1.5 block">Email</label>
          <input data-testid="letterhead-email" value={lh.email} onChange={set("email")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="label-eyebrow mb-1.5 block">Logo (PNG/JPG, under 600KB)</label>
          <input data-testid="letterhead-logo" type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="block w-full text-xs" />
          {lh.logo_data && (
            <div className="mt-2 flex items-center gap-2">
              <img src={lh.logo_data} alt="logo preview" className="h-12 rounded-md border border-border/60 bg-white p-1" />
              <button data-testid="letterhead-logo-remove" onClick={() => setLh((p) => ({ ...p, logo_data: "" }))} className="rounded-md border border-border/70 px-2 py-1 text-[11px] text-muted-foreground hover:text-destructive">Remove</button>
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow mb-1.5 block">Address (multi-line)</label>
          <textarea data-testid="letterhead-address" value={lh.address} onChange={set("address")} rows={2} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button data-testid="letterhead-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save letterhead"}
        </button>
        {msg && <span data-testid="letterhead-message" className="text-xs text-emerald-600">{msg}</span>}
        {err && <span className="text-xs text-destructive">{err}</span>}
      </div>
    </section>
  );
}

function BackupRestore() {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const download = async () => {
    setBusy("export"); setErr(""); setMsg("");
    try {
      const r = await api.get("/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hcit-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg("Backup downloaded. Keep it somewhere safe.");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const restore = async () => {
    if (!file) return;
    const warning = mode === "replace"
      ? "REPLACE mode will DELETE existing data in every collection found in the backup and re-insert from the file. This cannot be undone. Continue?"
      : "MERGE mode will update existing records by ID and insert missing ones. Continue?";
    if (!window.confirm(warning)) return;
    setBusy("restore"); setErr(""); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const r = await api.post("/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = Object.entries(r.data.results || {}).map(([k, v]) =>
        `${k}: ${v.mode === "replace" ? v.restored + " restored" : `${v.inserted} added, ${v.updated} updated`}`);
      setMsg(`Restore complete (${mode}). ${parts.slice(0, 6).join(" · ")}${parts.length > 6 ? " · …" : ""}`);
      setFile(null);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <section className="sharp-card mt-6 p-5">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
        <DatabaseBackup className="h-5 w-5" /> Backup &amp; Restore
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Download a full snapshot of all data (customers, tickets, AMCs, spares, orders, users and more) for data migration or safe-keeping — and restore it later.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-md border border-border/60 p-4">
          <p className="label-eyebrow">Full backup</p>
          <p className="mt-1 text-xs text-muted-foreground">Exports every collection into a single JSON file.</p>
          <button
            data-testid="backup-download"
            onClick={download}
            disabled={busy === "export"}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" /> {busy === "export" ? "Preparing…" : "Download backup"}
          </button>
        </div>

        <div className="rounded-md border border-border/60 p-4">
          <p className="label-eyebrow">Restore from backup</p>
          <input
            data-testid="backup-restore-file"
            type="file"
            accept=".json,application/json"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-2 block w-full text-xs"
          />
          <div className="mt-2 flex items-center gap-2">
            <select
              data-testid="backup-restore-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="rounded-md border border-input bg-background px-2.5 py-1.5 text-xs"
            >
              <option value="merge">Merge — update by ID, add missing</option>
              <option value="replace">Replace — wipe &amp; restore (danger)</option>
            </select>
            <button
              data-testid="backup-restore-submit"
              onClick={restore}
              disabled={!file || busy === "restore"}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-50"
            >
              <UploadCloud className="h-3.5 w-3.5" /> {busy === "restore" ? "Restoring…" : "Restore"}
            </button>
          </div>
          {mode === "replace" && (
            <p className="mt-2 flex items-center gap-1 text-[11px] text-amber-600">
              <AlertTriangle className="h-3 w-3" /> Replace deletes current data in the collections being restored.
            </p>
          )}
        </div>
      </div>

      {msg && <div data-testid="backup-message" className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">{msg}</div>}
      {err && <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
    </section>
  );
}

function CompanyProfiles() {
  const empty = { id: null, name: "", company_name: "", address: "", phone: "", email: "", logo_data: "" };
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState(null); // null = closed
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/settings/company-profiles").then((r) => setProfiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) { setErr("Logo must be under 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm((p) => ({ ...p, logo_data: reader.result })); setErr(""); };
    reader.readAsDataURL(f);
  };
  const save = async () => {
    if (!form?.name?.trim()) { setErr("Profile name is required"); return; }
    setBusy(true); setErr("");
    try { await api.post("/settings/company-profiles", form); setForm(null); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete company profile "${p.name}"?`)) return;
    try { await api.delete(`/settings/company-profiles/${p.id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <section className="sharp-card p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
          <ImageIcon className="h-5 w-5" /> Company Profiles (Quotation letterheads)
        </h2>
        <button data-testid="profile-add" onClick={() => setForm({ ...empty })}
          className="inline-flex items-center gap-1 rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-primary">
          <Plus className="h-3.5 w-3.5" /> Add profile
        </button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Run multiple companies? Add a profile for each — pick which company's letterhead appears on a quotation from its Preview screen.
      </p>

      {profiles.length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {profiles.map((p) => (
            <div key={p.id} data-testid={`profile-card-${p.id}`} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
              <div className="flex items-center gap-2 overflow-hidden">
                {p.logo_data && <img src={p.logo_data} alt="" className="h-8 w-8 rounded bg-white object-contain p-0.5" />}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{p.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{p.company_name || "—"}</p>
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <button data-testid={`profile-edit-${p.id}`} onClick={() => setForm({ ...empty, ...p })} className="rounded-md border border-border/70 px-2 py-1 text-[11px] text-muted-foreground hover:text-primary">Edit</button>
                <button data-testid={`profile-delete-${p.id}`} onClick={() => remove(p)} className="rounded-md border border-border/70 px-2 py-1 text-[11px] text-muted-foreground hover:text-destructive">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {profiles.length === 0 && !form && <p className="mt-4 text-xs text-muted-foreground">No company profiles yet.</p>}

      {form && (
        <div className="mt-4 grid gap-3 rounded-md border border-border/60 p-4 sm:grid-cols-2">
          <div>
            <label className="label-eyebrow mb-1.5 block">Profile name * (internal label)</label>
            <input data-testid="profile-name" value={form.name} onChange={set("name")} placeholder="e.g. HCIT Solutions" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Company name (on documents)</label>
            <input data-testid="profile-company" value={form.company_name || ""} onChange={set("company_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Phone</label>
            <input data-testid="profile-phone" value={form.phone || ""} onChange={set("phone")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Email</label>
            <input data-testid="profile-email" value={form.email || ""} onChange={set("email")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Address</label>
            <textarea data-testid="profile-address" value={form.address || ""} onChange={set("address")} rows={2} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Logo (PNG/JPG, max 1MB)</label>
            <input data-testid="profile-logo" type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="block w-full text-xs" />
            {form.logo_data && (
              <div className="mt-2 flex items-center gap-3">
                <img src={form.logo_data} alt="logo preview" className="h-12 rounded-md border border-border/60 bg-white p-1" />
                <button onClick={() => setForm((p) => ({ ...p, logo_data: "" }))} className="rounded-md border border-border/70 px-2 py-1 text-[11px] text-muted-foreground hover:text-destructive">Remove</button>
              </div>
            )}
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex gap-2 sm:col-span-2">
            <button data-testid="profile-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : (form.id ? "Update profile" : "Save profile")}
            </button>
            <button onClick={() => { setForm(null); setErr(""); }} className="rounded-md border border-border/70 px-3 py-2 text-xs">Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
